import { useOktaAuth } from '@okta/okta-react';
import { useQueryClient } from '@tanstack/react-query';
import { Icon, IconNames } from '@triumph/design-system';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useCurrentUserEmail } from '@/components/hooks/useCurrentUserEmail';
import { ProductNames } from '@/components/providers/ApplicantContextProvider/ApplicantContextProvider.types';
import { usePermissions } from '@/components/providers/PermissionsContextProvider';
import {
    ApplicantResponse,
    OpenConditionsFilter,
    ProductReviewType,
    SortableColumn,
    SortOrder,
} from '@/components/shared/api/endpoints/applicant';
import {
    Assignee,
    getApplicantsAssignees,
} from '@/components/shared/api/endpoints/applicantAssignees';
import {
    getApplicantsConditionCounts,
    OpenConditionCounts,
} from '@/components/shared/api/endpoints/applicantConditions';
import { useApplicantsQuery, useAssigneesQuery } from '@/components/shared/api/queries/applicant';
import { AssignCell } from '@/components/shared/components/AssignCell';
import { TabDataState } from '@/components/shared/components/TabDataState';
import { useFeatureFlags } from '@/components/shared/constants/featureFlags';
import { Feature } from '@/components/shared/constants/features';
import {
    EVENT_LOG_NAMES,
    getLoggingContext,
    honeybadgerLogEvent,
} from '@/components/shared/utils/logging';

import { format } from 'date-fns-tz';
import { isEmpty } from 'es-toolkit/compat';
import './ApplicantsQueue.css';
import { Container, PageTitle, TableWrapper } from './ApplicantsQueue.styles';
import {
    FILTER_REGISTRY,
    FilterDrawer,
    FilterDrawerGlobalStyles,
    FiltersToggle,
    UNASSIGNED_ASSIGNEE_CODE,
    UNASSIGNED_ASSIGNEE_OPTION,
    useFilters,
} from './Filters';

interface ApplicantDto extends Omit<ApplicantResponse, 'products'> {
    applicationAge: string;
    applicationStatus: string;
    productName: string;
    statusAge: string;
    submittedAt: string;
    openConditionCount: number;
    productAssigneeEmails: string;
    hasNoBorder?: boolean;
    // Stripes by applicant rather than by row, so an applicant's product rows share one color.
    isAlternateApplicant?: boolean;
    reviewText?: string;
}

export type OptionType = { name: string; code: string } | null;

const SORTABLE_COLUMNS: SortableColumn[] = ['statusAge', 'applicationAge', 'submittedAt'];
const SORT_ORDERS: SortOrder[] = ['asc', 'desc'];

interface SortParams {
    sortBy: SortableColumn | undefined;
    sortOrder: SortOrder | undefined;
}

// Both-or-neither, same as the createdAtRange filter.
const parseSortParams = (params: URLSearchParams): SortParams => {
    const sortBy = SORTABLE_COLUMNS.find((column) => column === params.get('sortBy'));
    const sortOrder = SORT_ORDERS.find((order) => order === params.get('sortOrder'));
    if (!sortBy || !sortOrder) return { sortBy: undefined, sortOrder: undefined };
    return { sortBy, sortOrder };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const addNoBorderClassname = (data: any) => {
    return data.hasNoBorder ? 'no-border' : '';
};

const toProductName = (name: string): ProductNames | undefined => {
    if (name === 'Factoring') return ProductNames.FACTORING;
    if (name === 'LoadPay') return ProductNames.LOADPAY;
    if (name === 'TcsFuel') return ProductNames.TCSFUEL;
    return undefined;
};

// Map a queue product (by backend productName) to its per-product assignee
// emails on the applicant
const productAssigneeEmailsFor = (
    productName: string,
    applicant: Pick<
        ApplicantResponse,
        'factoringAssigneeEmails' | 'loadPayAssigneeEmails' | 'tcsFuelAssigneeEmails'
    >,
): string => {
    const map: Record<string, string> = {
        Factoring: applicant.factoringAssigneeEmails,
        LoadPay: applicant.loadPayAssigneeEmails,
        TcsFuel: applicant.tcsFuelAssigneeEmails,
    };
    return map[productName] ?? '';
};

const REVIEW_READY_PRODUCT_NAMES = ['Factoring', 'LoadPay'];

const reviewTextFor = (
    productName: string,
    applicant: Pick<ApplicantResponse, 'readyForReview'>,
): string => {
    if (!REVIEW_READY_PRODUCT_NAMES.includes(productName)) return '-';

    const review = applicant.readyForReview?.find((r) => r.productName === productName);
    if (!review?.isReviewReady) return '-';

    return `${review.productName} ready for review`;
};

const openConditionCountFor = (productName: string, counts: OpenConditionCounts): number => {
    const map: Record<string, number> = {
        Factoring: counts.factoringOpenConditionCount,
        LoadPay: counts.loadPayOpenConditionCount,
        TcsFuel: counts.tcsFuelOpenConditionCount,
    };
    return map[productName] ?? 0;
};

export const ApplicantsQueue = () => {
    const { isSafeEmailViewsEnabled } = useFeatureFlags();
    const { hasFeature } = usePermissions();
    const { authState } = useOktaAuth();
    const currentUserEmail = useCurrentUserEmail();
    const queryClient = useQueryClient();

    const [searchParams, setSearchParams] = useSearchParams();
    const initialSort = parseSortParams(searchParams);
    const [applicants, setApplicants] = useState<ApplicantDto[]>([]);
    const [sortBy, setSortBy] = useState<SortableColumn | undefined>(initialSort.sortBy);
    const [sortOrder, setSortOrder] = useState<SortOrder | undefined>(initialSort.sortOrder);
    const [hoveredRow, setHoveredRow] = useState<string>('');
    const canViewApplicant = hasFeature(Feature.ViewApplicant);
    const hasLoadedOnce = useRef(false);

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const { data: assigneesData } = useAssigneesQuery();

    const assigneeOptions = useMemo(
        () => assigneesData?.map((email) => ({ name: email, code: email })) ?? [],
        [assigneesData],
    );

    const filterCtx = useMemo(() => ({ assigneeOptions }), [assigneeOptions]);
    const filtersResult = useFilters(filterCtx);

    const activeProduct = filtersResult.getApplied<OptionType[]>('product');
    const activePersona = filtersResult.getApplied<OptionType[]>('persona');
    const activeStatus = filtersResult.getApplied<OptionType[]>('status');
    const activeAssignee = filtersResult.getApplied<OptionType[]>('assignee');
    const activeUsDot = filtersResult.getApplied<string>('usDot');
    const activeBusinessNames = filtersResult.getApplied<string[]>('businessNames');
    const activeEmails = filtersResult.getApplied<string[]>('emails');
    const activeOwnerNames = filtersResult.getApplied<string[]>('ownerNames');
    const activeMcNumbers = filtersResult.getApplied<string[]>('mcNumbers');
    const activeFeins = filtersResult.getApplied<string[]>('feins');
    const activePhones = filtersResult.getApplied<string[]>('phones');
    const activeReviewType = filtersResult.getApplied<OptionType>('reviewType');
    const activeOpenConditions = filtersResult.getApplied<OptionType>('openConditions');

    const includeUnassigned = activeAssignee.some((o) => o?.code === UNASSIGNED_ASSIGNEE_CODE);
    const activeAssigneeEmails = activeAssignee
        .filter(
            (o): o is NonNullable<OptionType> => o !== null && o.code !== UNASSIGNED_ASSIGNEE_CODE,
        )
        .map((o) => o.code);

    const activeCreatedAtRange = filtersResult.getApplied<(Date | null)[] | null>('createdAtRange');
    const activeCreatedAtStart = activeCreatedAtRange?.[0]?.toISOString() ?? null;
    const activeCreatedAtEnd = activeCreatedAtRange?.[1]?.toISOString() ?? null;

    const {
        data: applicantsData,
        isFetching: isGetApplicantsPending,
        isError: isGetApplicantsError,
        refetch: refetchApplicants,
    } = useApplicantsQuery({
        productNames:
            activeProduct.length === 0
                ? null
                : activeProduct.filter((p) => p !== null).map((p) => parseInt(p.code)),
        personas:
            activePersona.length === 0
                ? null
                : activePersona.filter((p) => p !== null).map((p) => parseInt(p.code)),
        statusFilter:
            activeStatus.length === 0
                ? null
                : activeStatus.filter((s) => s !== null).map((s) => parseInt(s.code)),
        sortBy,
        sortOrder,
        assigneeEmails: activeAssigneeEmails.length > 0 ? activeAssigneeEmails : null,
        includeUnassigned: includeUnassigned || undefined,
        usDot: activeUsDot || undefined,
        businessNames: activeBusinessNames.length > 0 ? activeBusinessNames : null,
        createdAtStart: activeCreatedAtStart,
        createdAtEnd: activeCreatedAtEnd,
        emails: activeEmails.length > 0 ? activeEmails : null,
        feins: activeFeins.length > 0 ? activeFeins : null,
        mcNumbers: activeMcNumbers.length > 0 ? activeMcNumbers : null,
        ownerNames: activeOwnerNames.length > 0 ? activeOwnerNames : null,
        phones: activePhones.length > 0 ? activePhones : null,
        productReviewType: activeReviewType
            ? (parseInt(activeReviewType.code) as ProductReviewType)
            : undefined,
        openConditionsFilter: activeOpenConditions
            ? (parseInt(activeOpenConditions.code) as OpenConditionsFilter)
            : undefined,
    });

    // Refresh only the assignee fields for all currently visible rows in-place,
    // without re-running the full queue query (which could reorder or add rows).
    // Used for both successful assigns, conflicts, and the 30s assignee poll.
    const handleRefreshVisibleAssignees = useCallback(async () => {
        const uniqueIds = [...new Set(applicants.map((a) => a.applicantId))];
        if (uniqueIds.length === 0) return;

        // Fetch all assignees in one batch request instead of one GET per applicant,
        // which was overloading Postgres.
        const results = await getApplicantsAssignees(uniqueIds);
        const assigneeMap = new Map<string, Assignee[]>(Object.entries(results));

        let conditionCountsMap = new Map<string, OpenConditionCounts>();
        try {
            const counts = await getApplicantsConditionCounts(uniqueIds);
            conditionCountsMap = new Map<string, OpenConditionCounts>(Object.entries(counts));
        } catch (error) {
            const userId = authState?.idToken?.claims?.sub;
            honeybadgerLogEvent(EVENT_LOG_NAMES.QueueConditionCountsRefreshError, {
                level: 'ERROR',
                customAttributes: {
                    ...getLoggingContext(currentUserEmail, userId),
                    error: error instanceof Error ? error.message : String(error),
                },
            });
        }

        queryClient.setQueriesData<ApplicantResponse[]>({ queryKey: ['applicants'] }, (oldData) => {
            if (!oldData) return oldData;
            return oldData.map((applicant) => {
                const assignees = assigneeMap.get(applicant.applicantId);
                const counts = conditionCountsMap.get(applicant.applicantId);
                if (!assignees && !counts) return applicant;

                const patchedProducts = counts
                    ? applicant.products.map((product) => ({
                          ...product,
                          openConditionCount: openConditionCountFor(product.productName, counts),
                      }))
                    : applicant.products;

                if (!assignees) {
                    return { ...applicant, products: patchedProducts };
                }

                const factoring = assignees
                    .filter((a) => String(a.product).toUpperCase() === 'FACTORING')
                    .map((a) => a.email)
                    .join(', ');
                const loadPay = assignees
                    .filter((a) => String(a.product).toUpperCase() === 'LOADPAY')
                    .map((a) => a.email)
                    .join(', ');
                const tcsFuel = assignees
                    .filter((a) => String(a.product).toUpperCase() === 'TCSFUEL')
                    .map((a) => a.email)
                    .join(', ');
                return {
                    ...applicant,
                    products: patchedProducts,
                    factoringAssigneeEmails: factoring,
                    loadPayAssigneeEmails: loadPay,
                    tcsFuelAssigneeEmails: tcsFuel,
                    assigneeEmails: assignees.map((a) => a.email).join(', '),
                    assigneeCount: assignees.length,
                };
            });
        });
    }, [applicants, queryClient, authState, currentUserEmail]);

    // Window focus / tab focus — refresh assignee fields in-place so row order stays stable.
    // Listens to both visibilitychange (tab switch) and window focus (separate Chrome window)
    // so both focus paths are covered. Guard with hasLoadedOnce and a 1s debounce so both
    // events firing together on a tab switch don't trigger two concurrent fetches.
    useEffect(() => {
        let lastFocusRefreshAt = 0;

        const handleFocusRefresh = () => {
            if (!hasLoadedOnce.current) return;
            const now = Date.now();
            if (now - lastFocusRefreshAt < 1_000) return;
            lastFocusRefreshAt = now;
            handleRefreshVisibleAssignees();
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') handleFocusRefresh();
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocusRefresh);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocusRefresh);
        };
    }, [handleRefreshVisibleAssignees]);

    // 30s assignee polling while focused — refresh assignee cells in-place so row
    // order stays stable while a reviewer is actively working in the queue
    useEffect(() => {
        const interval = setInterval(() => {
            if (document.visibilityState === 'visible') {
                handleRefreshVisibleAssignees();
            }
        }, 30_000);
        return () => clearInterval(interval);
    }, [handleRefreshVisibleAssignees]);

    useEffect(() => {
        if (applicantsData) {
            hasLoadedOnce.current = true;
            const computedApplicants: ApplicantDto[] = applicantsData.reduce(
                (acc: ApplicantDto[], applicant, applicantIndex) => {
                    const isAlternateApplicant = applicantIndex % 2 === 1;

                    applicant.products.forEach((product, index) => {
                        const isFirstIndex = index === 0;
                        const isNotLastIndex = applicant.products.length !== index + 1;

                        const formattedSubmittedAt = isEmpty(product.submittedAt)
                            ? product.submittedAt
                            : format(product.submittedAt, 'h:mm a, MMM d, yyyy');

                        const productAssigneeEmails = productAssigneeEmailsFor(
                            product.productName,
                            applicant,
                        );

                        const reviewText = reviewTextFor(product.productName, applicant);

                        if (isFirstIndex) {
                            acc.push({
                                usDOT: applicant.usDOT,
                                partnerId: applicant.partnerId,
                                carrierName: applicant.carrierName,
                                assigneeEmails: applicant.assigneeEmails,
                                assigneeCount: applicant.assigneeCount,
                                factoringAssigneeEmails: applicant.factoringAssigneeEmails,
                                loadPayAssigneeEmails: applicant.loadPayAssigneeEmails,
                                tcsFuelAssigneeEmails: applicant.tcsFuelAssigneeEmails,
                                applicantId: applicant.applicantId,
                                lastUpdated: applicant.lastUpdated,
                                ...product,
                                submittedAt: formattedSubmittedAt,
                                productAssigneeEmails,
                                hasNoBorder: isNotLastIndex,
                                isAlternateApplicant,
                                reviewText,
                            });
                        } else {
                            acc.push({
                                ...product,
                                usDOT: '',
                                partnerId: '',
                                carrierName: '',
                                assigneeEmails: '',
                                assigneeCount: 0,
                                factoringAssigneeEmails: '',
                                loadPayAssigneeEmails: '',
                                tcsFuelAssigneeEmails: '',
                                applicantId: applicant.applicantId,
                                lastUpdated: '',
                                productAssigneeEmails,
                                hasNoBorder: isNotLastIndex,
                                isAlternateApplicant,
                                submittedAt: formattedSubmittedAt,
                                reviewText,
                            });
                        }
                    });

                    return acc;
                },
                [],
            );

            setApplicants(computedApplicants);
        }
    }, [applicantsData]);

    const applySort = (
        nextSortBy: SortableColumn | undefined,
        nextSortOrder: SortOrder | undefined,
    ) => {
        setSortBy(nextSortBy);
        setSortOrder(nextSortOrder);
        setSearchParams(
            (current) => {
                const params = new URLSearchParams(current);
                if (nextSortBy && nextSortOrder) {
                    params.set('sortBy', nextSortBy);
                    params.set('sortOrder', nextSortOrder);
                } else {
                    params.delete('sortBy');
                    params.delete('sortOrder');
                }
                return params;
            },
            { replace: true },
        );
    };

    const handleSort = (column: SortableColumn) => {
        if (sortBy === column) {
            // Toggle sort order if clicking the same column
            if (sortOrder === 'asc') {
                applySort(column, 'desc');
            } else if (sortOrder === 'desc') {
                // Clear sort if clicking desc column
                applySort(undefined, undefined);
            } else {
                applySort(column, 'asc');
            }
        } else {
            // New column, start with ascending
            applySort(column, 'asc');
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleRowClick = (event: any) => {
        if (!canViewApplicant) return;

        const userId = authState?.idToken?.claims?.sub;

        honeybadgerLogEvent(EVENT_LOG_NAMES.OpenApplicantClick, {
            level: 'INFO',
            customAttributes: {
                ...getLoggingContext(currentUserEmail, userId),
                currentPage: window.location.pathname,
                timestamp: new Date().toISOString(),
                applicantId: event.data.applicantId,
            },
        });

        const applicantUrl = `/applicant/${event.data.applicantId}`;
        if (isSafeEmailViewsEnabled) {
            window.open(applicantUrl, '_blank', 'noopener,noreferrer');
        } else {
            window.open(applicantUrl, '_blank');
        }
    };

    const renderSortIcon = (column: SortableColumn) => {
        if (sortBy !== column) {
            return (
                <span
                    style={{
                        opacity: 0.4,
                        display: 'inline-flex',
                        flexDirection: 'column',
                        marginLeft: '0.5rem',
                    }}
                >
                    <span data-testid={`sort-up-${column}`}>
                        <Icon name={IconNames.Arrow_CaretUp} size={22} id={`sort-up-${column}`} />
                    </span>
                    <span
                        style={{ marginTop: '-6px', display: 'inline-flex' }}
                        data-testid={`sort-down-${column}`}
                    >
                        <Icon
                            name={IconNames.Arrow_CaretDown}
                            size={22}
                            id={`sort-down-${column}`}
                        />
                    </span>
                </span>
            );
        }
        if (sortOrder === 'asc') {
            return (
                <span
                    style={{ display: 'inline-flex', marginLeft: '0.5rem' }}
                    data-testid={`sort-asc-${column}`}
                >
                    <Icon name={IconNames.Arrow_CaretUp} size={24} id={`sort-asc-${column}`} />
                </span>
            );
        }
        return (
            <span
                style={{ display: 'inline-flex', marginLeft: '0.5rem' }}
                data-testid={`sort-desc-${column}`}
            >
                <Icon name={IconNames.Arrow_CaretDown} size={24} id={`sort-desc-${column}`} />
            </span>
        );
    };

    const sortableHeader = (title: string, column: SortableColumn) => (
        <div
            onClick={() => handleSort(column)}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
        >
            <span>{title}</span>
            {renderSortIcon(column)}
        </div>
    );

    const assigneeBodyTemplate = (rowData: ApplicantDto) => {
        if (!rowData.productAssigneeEmails) {
            return '-';
        }

        const emails = rowData.productAssigneeEmails
            .split(',')
            .map((e) => e.trim())
            .filter((e) => e);
        const emailCount = emails.length;

        if (emailCount > 1) {
            return (
                <div title={rowData.productAssigneeEmails}>
                    {emails[0]} (+{emailCount - 1} more)
                </div>
            );
        }

        return rowData.productAssigneeEmails;
    };

    const logQueueFilterEvent = useCallback(
        (eventName: EVENT_LOG_NAMES, extra: Record<string, unknown> = {}) => {
            const userId = authState?.idToken?.claims?.sub;
            honeybadgerLogEvent(eventName, {
                level: 'INFO',
                customAttributes: {
                    ...getLoggingContext(currentUserEmail, userId),
                    ...extra,
                },
            });
        },
        [authState],
    );

    // Wrap apply/reset/removeApplied so every user-driven filter mutation
    // emits a PageAction. Pass the wrapped result to both the drawer and the
    // toggle so inline chip removes are also captured.
    const instrumentedFiltersResult = useMemo(
        () => ({
            ...filtersResult,
            apply: () => {
                logQueueFilterEvent(EVENT_LOG_NAMES.QueueFiltersApply, {
                    appliedFilterIds: filtersResult.appliedChips.map((c) => c.id),
                });
                filtersResult.apply();
            },
            reset: () => {
                logQueueFilterEvent(EVENT_LOG_NAMES.QueueFiltersReset);
                filtersResult.reset();
            },
            removeApplied: (id: string) => {
                logQueueFilterEvent(EVENT_LOG_NAMES.QueueFiltersChipRemove, { filterId: id });
                filtersResult.removeApplied(id);
            },
        }),
        [filtersResult, logQueueFilterEvent],
    );

    return (
        <Container>
            <FilterDrawerGlobalStyles />
            <PageTitle>Applicant Queue</PageTitle>
            <FiltersToggle
                isOpen={isDrawerOpen}
                onToggle={() => setIsDrawerOpen((o) => !o)}
                appliedCount={instrumentedFiltersResult.appliedCount}
                appliedChips={instrumentedFiltersResult.appliedChips}
                onRemoveChip={instrumentedFiltersResult.removeApplied}
                onOverflowClick={() => setIsDrawerOpen(true)}
            />
            <FilterDrawer
                isOpen={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                registry={FILTER_REGISTRY}
                useFiltersResult={instrumentedFiltersResult}
                rendererPropsByFilterId={{
                    assignee: { options: [UNASSIGNED_ASSIGNEE_OPTION, ...assigneeOptions] },
                }}
            />
            <TableWrapper>
                <div className="card">
                    <DataTable
                        value={isGetApplicantsError ? [] : applicants}
                        tableStyle={{ minWidth: '50rem' }}
                        loading={isGetApplicantsPending}
                        emptyMessage={
                            isGetApplicantsError ? (
                                <TabDataState
                                    isLoading={false}
                                    isLoadingError
                                    onRetry={() => refetchApplicants()}
                                    errorMessage="Could not load applicants."
                                    testId="applicants-queue"
                                >
                                    {null}
                                </TabDataState>
                            ) : (
                                'No applicants found'
                            )
                        }
                        onRowClick={handleRowClick}
                        onRowMouseEnter={(e) =>
                            canViewApplicant && setHoveredRow(e.data.applicantId)
                        }
                        onRowMouseLeave={() => setHoveredRow('')}
                        rowClassName={(data) => ({
                            'alternate-applicant': data.isAlternateApplicant,
                            'hovered-row': canViewApplicant && data.applicantId === hoveredRow,
                        })}
                    >
                        <Column
                            field="usDOT"
                            header="Us DOT"
                            bodyClassName={addNoBorderClassname}
                        ></Column>
                        <Column
                            field="partnerId"
                            header="Partner Id"
                            bodyClassName={addNoBorderClassname}
                        ></Column>
                        <Column
                            field="carrierName"
                            header="Business Name"
                            bodyClassName={addNoBorderClassname}
                        ></Column>
                        <Column
                            field="productName"
                            header="Product"
                            bodyClassName={addNoBorderClassname}
                        ></Column>
                        <Column
                            field="applicationStatus"
                            header="Application Status"
                            bodyClassName={addNoBorderClassname}
                        ></Column>
                        <Column
                            field="statusAge"
                            header={sortableHeader('Status Age', 'statusAge')}
                            bodyClassName={addNoBorderClassname}
                        ></Column>
                        <Column
                            field="applicationAge"
                            header={sortableHeader('Application Age', 'applicationAge')}
                            bodyClassName={addNoBorderClassname}
                        ></Column>
                        <Column
                            field="submittedAt"
                            header={sortableHeader('Submitted At', 'submittedAt')}
                            bodyClassName={addNoBorderClassname}
                        ></Column>
                        <Column
                            field="openConditionCount"
                            header="Open Conditions"
                            body={(rowData: ApplicantDto) =>
                                rowData.openConditionCount > 0 ? rowData.openConditionCount : '-'
                            }
                            bodyClassName={addNoBorderClassname}
                        ></Column>
                        <Column
                            header="Assignee"
                            body={(rowData: ApplicantDto) => {
                                const product = toProductName(rowData.productName);
                                if (!product) return assigneeBodyTemplate(rowData);
                                const assigneeEmail =
                                    rowData.productAssigneeEmails?.split(',')[0]?.trim() ||
                                    undefined;
                                return (
                                    <AssignCell
                                        applicantId={rowData.applicantId}
                                        product={product}
                                        currentAssigneeEmail={assigneeEmail}
                                        onSuccess={handleRefreshVisibleAssignees}
                                        onConflict={handleRefreshVisibleAssignees}
                                    />
                                );
                            }}
                            bodyClassName={addNoBorderClassname}
                        ></Column>
                        <Column
                            header="Review Ready"
                            bodyClassName={addNoBorderClassname}
                            field="reviewText"
                        ></Column>
                    </DataTable>
                </div>
            </TableWrapper>
        </Container>
    );
};
